import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Moon,
  LogOut,
  ArrowLeft,
  ChevronDown,
  Archive,
  Inbox,
  Link2,
  Database,
  Send,
  LayoutDashboard,
  Plus,
  Users,
  UserSearch,
  IdCard,
  Bot,
  BookOpen,
  ClipboardCheck,
  FileUp,
  FlaskConical,
  MessageSquareText,
  Settings2,
} from "lucide-react";
import { ClockLoader } from "react-spinners";
import useRequest from "../../hook/use-req";
import { cn } from "../../../utils/function";
import { NavigationApiResponse, ResourceNode, UserResponse } from "../../types";
import { CallsIcon, CDPIcon, ChartIcon, ChatIcon, DealsIcon, LeadIcon, TaskNavIcon, TeamIcon, TicketIcon, UserAddIcon, IntegrationsIcon } from "../../../assets/icon";
import { useTheme } from "../../../context/theme-context";
import { getStoredOrgRole } from "../../../utils/org-role";
import { FaBusinessTime } from "react-icons/fa";
import SuiteSwitcher from "../suite-switcher";
import { canSeeSupportAgentScreen, pendingCount, useSupportBotDocs } from "../../../views/crm-pages/support-bot/store";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  path?: string;
  children?: NavItem[];
  disabled?: boolean;
  badge?: "approvals";
}

interface NavMetaEntry {
  icon: React.ComponentType<any>;
  path?: string;
  children?: Record<string, NavMetaEntry>;
  // Shown greyed out and not clickable — a backend node whose screen isn't
  // built yet (Support Bot's Eval Gate / Feedback).
  disabled?: boolean;
  // A live count shown next to the label.
  badge?: "approvals";
}

// The tree shape (labels/nesting) comes entirely from the backend
// (GET /auth/resources/crm — payload.navigation.subnodes). This map only
// supplies the presentation details the API doesn't carry: which icon and
// which frontend route each label corresponds to. An item the backend sends
// that isn't listed here still renders (generic icon, no link) instead of
// disappearing, so a new backend-added item is visible immediately.
const NAV_META: Record<string, NavMetaEntry> = {
  Home: { icon: TaskNavIcon, path: "/crm/dashboard" },
  Conversations: {
    icon: ChatIcon,
    children: {
      Inbox: { icon: Inbox, path: "/chats" },
      Archived: { icon: Archive, path: "/archive/chats" },
    },
  },
  CDP: {
    icon: CDPIcon,
    children: {
      Overview: { icon: CDPIcon, path: "/cdp" },
      Connections: {
        icon: Link2,
        path: "/cdp/connections",
        children: {
          Overview: { icon: LayoutDashboard, path: "/cdp/connections" },
          Sources: { icon: Database, path: "/cdp/connections/sources" },
          Destinations: { icon: Send, path: "/cdp/connections/destinations" },
          "New Connection": { icon: Plus, path: "/cdp/connections/new" },
        },
      },
      Unify: { icon: Users, path: "/cdp/unify" },
      "Profile Explorer": { icon: UserSearch, path: "/cdp/profile-explorer" },
    },
  },
  Businesses: { icon: FaBusinessTime, path: "/businesses" },
  Calls: { icon: CallsIcon, path: "/calls" },
  Leads: { icon: LeadIcon, path: "/leads" },
  Customers: { icon: UserAddIcon, path: "/customers" },
  Analytics: { icon: ChartIcon, path: "/analytics" },
  Team: { icon: TeamIcon, path: "/team" },
  Ticket: { icon: TicketIcon, path: "/ticketing" },
  Deals: { icon: DealsIcon, path: "/deals" },
  Traits: { icon: IdCard, path: "/traits" },
  Integrations: { icon: IntegrationsIcon, path: "/integrations" },
  "Support Agent": {
    icon: Bot,
    children: {
      Knowledge: { icon: BookOpen, path: "/support-bot/knowledge" },
      Approvals: { icon: ClipboardCheck, path: "/support-bot/approvals", badge: "approvals" },
      "My Submissions": { icon: FileUp, path: "/support-bot/my-submissions" },
      "Eval Gate": { icon: FlaskConical, disabled: true },
      Feedback: { icon: MessageSquareText, disabled: true },
      Setup: { icon: Settings2, path: "/support-bot/setup" },
    },
  },
};

// Order Support Bot's screens the way the design lists them (built ones
// first) rather than the backend's own key order.
const CHILD_ORDER: Record<string, string[]> = {
  "Support Bot": ["Knowledge", "Approvals", "My Submissions", "Eval Gate", "Feedback", "Setup"],
};

// The backend has labelled this group both ways; its screens are filtered
// per org_role (canSeeSupportAgentScreen).
const SUPPORT_AGENT_LABELS = ["Support Agent", "Support Bot"];

const slugify = (label: string) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const transformNavTree = (
  subnodes: Record<string, ResourceNode>,
  metaLevel: Record<string, NavMetaEntry> = NAV_META,
  order?: string[]
): NavItem[] => {
  const entries = Object.entries(subnodes);
  if (order) entries.sort(([a], [b]) => (order.indexOf(a) + 1 || 99) - (order.indexOf(b) + 1 || 99));
  return entries
    .filter(([, node]) => !node.allowed || node.allowed === "view")
    .map(([label, node]) => {
      const meta = metaLevel[label];
      const hasChildren = !!node.subnodes && Object.keys(node.subnodes).length > 0;
      const item: NavItem = {
        id: slugify(label),
        label,
        icon: meta?.icon || ChatIcon,
        path: meta?.path,
        disabled: meta?.disabled,
        badge: meta?.badge,
      };
      if (hasChildren) {
        item.children = transformNavTree(node.subnodes, meta?.children || {}, CHILD_ORDER[label]);
      }
      return item;
    });
};

const CrmSidebar: React.FC<SidebarProps> = ({ open, onToggle }) => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark: darkMode, toggleTheme } = useTheme();
  const [user, setUser] = useState<UserResponse | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [resources, setResources] = useState<NavigationApiResponse | null>(null);
  const role = user?.payload?.role || localStorage.getItem("role") || "";
  const orgRole = user?.payload?.org_role || getStoredOrgRole();
  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getResourceData, loading } = useRequest(`/auth/resources/crm`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getResourceData();
      if (response) {
        setResources(response);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!token) {
      const reloadCount = Number(sessionStorage.getItem("reloadCount") || "0");
      if (reloadCount < 1) {
        sessionStorage.setItem("reloadCount", String(reloadCount + 1));
        window.location.reload();
      } else {
        sessionStorage.removeItem("reloadCount");
      }
    }
  }, [token, navigate]);

  const handleSignOut = async () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("resetOtpCode");
    localStorage.removeItem("role");
    navigate("/");
  };

  const approvalsCount = pendingCount(useSupportBotDocs());

  const navigationItems: NavItem[] = (resources?.payload?.navigation?.subnodes
    ? transformNavTree(resources.payload.navigation.subnodes)
    : []
  ).map((item) =>
    SUPPORT_AGENT_LABELS.includes(item.label) && item.children
      ? { ...item, children: item.children.filter((c) => canSeeSupportAgentScreen(c.label)) }
      : item
  );

  const isActive = (path?: string) => !!path && location.pathname === path;

  const isBranchActive = (item: NavItem): boolean => {
    if (isActive(item.path)) return true;
    return item.children?.some(isBranchActive) ?? false;
  };

  const toggleSection = (id: string) => {
    setExpandedSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  // Expandable groups need the sidebar open to show their contents — if
  // it's collapsed, expand it first and let the toggle land once the
  // width transition has room to show the dropdown (same timing the old
  // hardcoded toggleConversation/toggleCdp used).
  const handleExpandableClick = (id: string) => {
    if (open) {
      toggleSection(id);
    } else {
      onToggle();
      setTimeout(() => toggleSection(id), 300);
    }
  };

  const renderNavItem = (item: NavItem, level = 0): React.ReactNode => {
    const Icon = item.icon;
    const hasChildren = !!item.children && item.children.length > 0;
    const isExpanded = expandedSections.includes(item.id);
    const active = isBranchActive(item);

    const iconSizeClass = level === 0 ? "w-6 h-6" : level === 1 ? "w-[16px] h-[16px]" : "w-[14px] h-[14px]";
    const rowPaddingClass = level === 0 ? "py-2.5" : "py-2";

    const row = (
      <div
        onClick={hasChildren ? () => handleExpandableClick(item.id) : undefined}
        className={cn(
          `flex items-center px-3 ${rowPaddingClass} rounded-lg text-sm font-semibold transition-all duration-200 select-none`,
          item.disabled ? "cursor-default" : "cursor-pointer",
          open ? "gap-3.5 justify-start" : "justify-center",
          item.disabled
            ? "text-gray-300 dark:text-gray-600"
            : active
            ? level === 0
              ? "text-gray-900 dark:text-white"
              : "text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-800"
            : "text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
        )}
      >
        <Icon
          className={cn(
            iconSizeClass,
            "shrink-0",
            item.disabled ? "text-gray-300 dark:text-gray-600" : active ? "text-gray-800 dark:text-gray-100" : "text-[#7B7B7B] font-semibold dark:text-gray-500"
          )}
          strokeWidth={active ? 2 : 1.75}
        />
        {open && (
          hasChildren ? (
            <div className="flex items-center justify-between flex-1">
              <span className="tracking-tight">{item.label}</span>
              <ChevronDown
                className={cn(
                  "w-4 h-4 transition-transform duration-200",
                  isExpanded ? "rotate-180" : ""
                )}
              />
            </div>
          ) : (
            <span className="flex items-center justify-between flex-1 tracking-tight">
              {item.label}
              {item.badge === "approvals" && approvalsCount > 0 && (
                <span className="ml-2 min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-semibold flex items-center justify-center">{approvalsCount}</span>
              )}
            </span>
          )
        )}
      </div>
    );

    return (
      <div key={item.id} className="block">
        {hasChildren || item.disabled ? row : <Link to={item.path || "#"} className="block">{row}</Link>}

        {hasChildren && open && isExpanded && (
          <div className="ml-2 mt-1 space-y-0.5 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
            {item.children!.map((child) => renderNavItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col h-full m-3 rounded-lg bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 transition-all duration-300 ${open ? "w-[257px]" : "w-[64px]"
        }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        {!open && (
          <div className="flex flex-col items-center justify-center gap-2 w-7">
            <img
              className="w-5 h-5"
              src={user?.payload?.org_logo || "/default-logo.png"}
              alt="logo"
            />
            <SuiteSwitcher activeSuite="crm" open={open} onExpandSidebar={onToggle} role={role} orgRole={orgRole} />
            <button
              className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={onToggle}
              title={open ? "Collapse" : "Expand"}
            >
              <ArrowLeft
                className={`w-3.5 h-3.5 transition-transform ${!open ? "rotate-180" : ""
                  }`}
              />
            </button>
          </div>
        )}

        {open && (
          <div className="flex items-center gap-1.5">
            <img
              className="w-5 h-5"
              src={user?.payload?.org_logo || "/default-logo.png"}
              alt="logo"
            />
            <SuiteSwitcher activeSuite="crm" open={open} onExpandSidebar={onToggle} role={role} orgRole={orgRole} />

            {/* Back arrow */}
            <button
              className="ml-auto flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              onClick={onToggle}
              title={open ? "Collapse" : "Expand"}
            >
              <ArrowLeft
                className={`w-3.5 h-3.5 transition-transform ${!open ? "rotate-180" : ""
                  }`}
              />
            </button>
          </div>
        )}
      </div>

      {/* Navigation — entirely backend-driven, see NAV_META/transformNavTree above */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {loading ? (
          <div className="flex justify-center mt-10">
            <ClockLoader color="#4F46E5" loading={loading} size={32} />
          </div>
        ) : (
          navigationItems.map((item) => renderNavItem(item))
        )}
      </nav>

      {/* Bottom Section */}
      <div className="px-3 py-4 border-t border-gray-100 dark:border-gray-800 space-y-0.5">
        {/* Dark Mode Toggle */}
        <div
          className={cn(
            "flex items-center px-3 py-2.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
            open ? "justify-between" : "justify-center"
          )}
        >
          <div className="flex items-center gap-3.5">
            <Moon
              className="w-[18px] h-[18px] text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0"
              strokeWidth={1.75}
            />
            {open && (
              <span className="text-sm font-semibold text-gray-700 dark:text-white tracking-tight">
                Dark Mode
              </span>
            )}
          </div>

          {/* Toggle Switch */}
          {open && (
            <button
              onClick={toggleTheme}
              className={cn(
                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none shrink-0",
                darkMode ? "bg-blue-500" : "bg-gray-200 dark:bg-gray-700"
              )}
              aria-label="Toggle dark mode"
            >
              <span
                className={cn(
                  "inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200",
                  darkMode ? "translate-x-[18px]" : "translate-x-1"
                )}
              />
            </button>
          )}
        </div>

        {/* Log Out */}
        <button
          className="flex w-full items-center gap-3.5 px-3 py-2.5 rounded-lg text-sm font-semibold text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          onClick={handleSignOut}
        >
          <LogOut
            className="w-[18px] h-[18px] text-[#7B7B7B] font-semibold dark:text-gray-500 shrink-0"
            strokeWidth={1.75}
          />
          {open && <span className="tracking-tight">Log Out</span>}
        </button>
      </div>
    </div>
  );
};

export default CrmSidebar;
